import { useEffect, useState } from 'react';
import { adminService } from '@/services/adminService';
import { AdminDashboardStats } from '@/types/admin';
import {
  Users,
  Activity,
  BookOpen,
  TrendingUp,
  AlertTriangle,
  Timer,
  CalendarDays,
  Flame,
  Star
} from 'lucide-react';

export function AdminOverviewCards() {
  const [stats, setStats] = useState<AdminDashboardStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const data = await adminService.getDashboardStats();
      setStats(data);
    } catch (error) {
      console.error('Failed to load stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading || !stats) {
    return <div className="text-gray-500">Loading statistics...</div>;
  }

  const formatTime = (seconds: number) => {
    if (!seconds) return '0h';

    if (seconds < 3600) {
      const minutes = Math.round(seconds / 60);
      return `${minutes}m`;
    }

    const hours = seconds / 3600;
    return `${hours.toFixed(1)}h`;
  };

  const cards = [
    {
      title: 'Total Users',
      value: stats.totalUsers.toLocaleString(),
      detail: `${stats.newUsers.toLocaleString()} new this week`,
      icon: Users,
      color: 'text-blue-600'
    },
    {
      title: 'Active This Week',
      value: stats.activeThisWeek.toLocaleString(),
      detail: `${stats.engagementRate}% engagement`,
      icon: Activity,
      color: 'text-green-600'
    },
    {
      title: 'Highly Active Today',
      value: stats.highlyActiveUsers.toLocaleString(),
      detail: `${stats.activeToday.toLocaleString()} active today`,
      icon: Flame,
      color: 'text-orange-600'
    },
    {
      title: 'At-Risk (14+ days)',
      value: stats.atRiskUsers.toLocaleString(),
      detail: 'Consider outreach to re-engage',
      icon: AlertTriangle,
      color: 'text-yellow-600'
    },
    {
      title: 'Learning Progress',
      value: stats.totalLessonsCompleted.toLocaleString(),
      detail: 'lessons completed',
      icon: BookOpen,
      color: 'text-purple-600'
    },
    {
      title: 'Time on Platform',
      value: formatTime(stats.totalTimeSpentSeconds),
      detail: 'total learning time',
      icon: Timer,
      color: 'text-rose-600'
    },
    {
      title: 'Monthly Activity',
      value: stats.activeThisMonth.toLocaleString(),
      detail: `${stats.totalEvents.toLocaleString()} total events`,
      icon: CalendarDays,
      color: 'text-indigo-600'
    },
    {
      title: 'New Users (7d)',
      value: stats.newUsers.toLocaleString(),
      detail: `${stats.totalUsers ? Math.round((stats.newUsers / Math.max(stats.totalUsers, 1)) * 100) : 0}% of total`,
      icon: Star,
      color: 'text-teal-600'
    },
    {
      title: 'Engagement Rate',
      value: `${stats.engagementRate}%`,
      detail: `${stats.activeThisWeek.toLocaleString()} users active this week`,
      icon: TrendingUp,
      color: 'text-amber-600'
    }
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
      {cards.map((card, idx) => {
        const Icon = card.icon;
        return (
          <div
            key={idx}
            className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm hover:shadow-md transition-shadow"
          >
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">{card.title}</p>
                <p className="text-2xl font-bold text-gray-900 mt-2">{card.value}</p>
                {card.detail && (
                  <p className="text-xs text-gray-500 mt-1">{card.detail}</p>
                )}
              </div>
              <Icon className={'w-8 h-8 ' + card.color} />
            </div>
          </div>
        );
      })}
    </div>
  );
}
